import * as Yup from "yup";

import { findEspmCategoryForValidation } from "./espmTaxonomyValidationContext";

/**
 * An optional free-text field that has to reach the API as a number (bug 4044).
 *
 * Thousands separators are accepted -- people type "40,000" for a square
 * footage and the mapper strips the commas -- but anything else ("12,000 sq
 * ft", "approx 4000") has to be caught here. Without a rule the mapper's
 * Number() produced NaN, which serialized to null, and the value disappeared
 * on save with nothing shown to the owner.
 */
const optionalNumericField = (message: string, allowDecimal = false) =>
  Yup.string()
    .optional()
    .test("plain-number", message, (value) => {
      if (value == null) {
        return true;
      }
      const cleaned = value.replace(/,/g, "").trim();
      if (cleaned === "") {
        return true;
      }
      return allowDecimal
        ? /^\d+(\.\d+)?$/.test(cleaned)
        : /^\d+$/.test(cleaned);
    });

export const PropertyValidationSchema = Yup.object({
  propertyName: Yup.string().required("Property name is required"),
  file: Yup.array().of(Yup.mixed()),
  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zip: Yup.string()
    .required("Zipcode is required")
    .matches(/^\d{5}$/, "Enter a 5-digit zip code")
    .test("zip-plausible", "Enter a valid zip code", (value) => {
      const n = Number(value);
      return n >= 501 && n <= 99950;
    }),
  land: Yup.string().oneOf(
    ["Owned", "Leased"],
    "Land type must be 'Owned' or 'Leased'"
  ),
  hvacPrimaryTypeIds: Yup.array()
    .of(Yup.number())
    .min(1, "Select at least one HVAC system type")
    .required("HVAC system type is required"),
  espmPropertyTypeCategoryId: Yup.number()
    .transform((curr, orig) => (orig === "" || orig == null ? undefined : curr))
    .typeError("Property type is required")
    .required("Property type is required"),
  espmPropertyTypeDetailId: Yup.number()
    .nullable()
    .transform((curr, orig) => (orig === "" || orig == null ? null : curr))
    .test(
      "requires-subtype",
      "Property subtype is required for this category",
      function (value) {
        const categoryId = this.parent.espmPropertyTypeCategoryId as
          | number
          | undefined;
        const category = findEspmCategoryForValidation(categoryId);
        if (!category?.requiresSubtype) {
          return true;
        }
        return value != null && Number(value) > 0;
      }
    ),
  mixedUseComponents: Yup.array()
    .of(
      Yup.object({
        detailId: Yup.number().required(),
        allocationPercent: Yup.number().nullable(),
        allocationSqFt: Yup.number().nullable(),
      })
    )
    .nullable()
    .test(
      "mixed-use-required",
      "Add at least one mixed-use component with a type and allocation",
      function (value) {
        const categoryId = this.parent.espmPropertyTypeCategoryId as
          | number
          | undefined;
        const category = findEspmCategoryForValidation(categoryId);
        if (!category?.usesAllocation) {
          return true;
        }
        const rows = value ?? [];
        const validRows = rows.filter(
          (r) => r?.detailId != null && Number(r.detailId) > 0
        );
        return validRows.length >= 1;
      }
    )
    .test(
      "mixed-use-allocation-100",
      "Mixed-use allocations must total 100%",
      function (value) {
        const categoryId = this.parent.espmPropertyTypeCategoryId as
          | number
          | undefined;
        const category = findEspmCategoryForValidation(categoryId);
        if (!category?.usesAllocation) {
          return true;
        }
        const rows = value ?? [];
        const total = rows.reduce((sum, row) => {
          const n = Number(row?.allocationPercent);
          return sum + (Number.isFinite(n) ? n : 0);
        }, 0);
        return Math.round(total) === 100;
      }
    )
    .test(
      "mixed-use-unique-components",
      "Each mixed-use component can only be selected once",
      function (value) {
        const categoryId = this.parent.espmPropertyTypeCategoryId as
          | number
          | undefined;
        const category = findEspmCategoryForValidation(categoryId);
        if (!category?.usesAllocation) {
          return true;
        }
        const detailIds = (value ?? [])
          .map((row) => Number(row?.detailId))
          .filter((id) => Number.isFinite(id) && id > 0);
        return detailIds.length === new Set(detailIds).size;
      }
    ),
  paysBill: Yup.string()
    .transform((curr, orig) => (orig === "" ? undefined : curr))
    .oneOf(
      ["Owner", "Occupant", "Tenant-Resident"],
      "Select who pays the energy bill"
    )
    .optional(),
  yearBuilt: Yup.string()
    .required("Year built is required")
    .matches(/^\d{4}$/, "Enter a 4-digit year"),
  sqft: optionalNumericField("Enter total square feet as a whole number"),
  roofSize: optionalNumericField("Enter roof size as a whole number"),
  units: optionalNumericField("Enter the number of units as a whole number"),
  floors: optionalNumericField("Enter the number of floors as a whole number"),
  acreage: optionalNumericField("Enter land acreage as a number", true),
  bin: Yup.string()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .matches(/^\d{7}$/, "BIN must be exactly 7 digits")
    .optional(),
  bbl: Yup.string()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .matches(/^\d{10}$/, "BBL must be exactly 10 digits")
    .optional(),
  priorWorkTypeIds: Yup.array()
    .of(Yup.number())
    .min(
      1,
      "Please select at least one prior retrofit / upgrade work type (choose 'None' if not applicable)"
    )
    .required("Prior retrofit and building upgrade work is required"),
  // "Not sure" (only offered when the lookup is AMBIGUOUS) leaves the provider blank on purpose.
  electricUtilityProvider: Yup.string()
    .trim()
    .when("electricUtilityNotSure", {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Confirm the electric utility"),
    })
    .test(
      "not-no-piped-gas",
      "Confirm the electric utility",
      (value) => {
        const v = (value ?? "").trim().toLowerCase();
        return v !== "none" && v !== "n/a" && v !== "na" && v !== "no piped gas";
      }
    ),
  gasUtilityProvider: Yup.string()
    .trim()
    .when("gasUtilityNotSure", {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Confirm the gas utility or choose No piped gas"),
    }),
});
